import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import apiClient from '../api/client';
import { PROMO_30_KITCHEN } from '../lib/promoCopy';
import { toast } from "sonner";
import { Loader2, RefreshCw, Settings, AlertTriangle, Sliders, LogOut, CheckCircle, XCircle, Mail, TestTube, Download } from "lucide-react";
import { useNavigate } from 'react-router-dom';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorTest, setAnchorTest] = useState(null);
  const [anchorLoading, setAnchorLoading] = useState(false);
  const [acStatus, setAcStatus] = useState(null);
  const [acEmails, setAcEmails] = useState(null);
  const [acLoading, setAcLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resCases, resLeads, resSettings, resConfig] = await Promise.all([
        apiClient.get('/cases', { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` } }),
        apiClient.get('/quick-leads', { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` } }).catch(() => ({ data: [] })),
        apiClient.get('/settings').catch(() => ({ data: { email_enabled: true } })),
        /** AC-status via /config (finns alltid); /activecampaign/status är valfri duplicering */
        apiClient.get('/config').catch(() => ({ data: {} })),
      ]);
      if (typeof resCases.data === "string" && /<!doctype html|<html/i.test(resCases.data)) {
        throw new Error(
          "API-svaret var HTML (index.html) istället för JSON. Kontrollera att /api proxas till backend i produktion, eller sätt REACT_APP_BACKEND_URL till backend-URL."
        );
      }
      setCases(Array.isArray(resCases.data) ? resCases.data : []);
      setLeads(Array.isArray(resLeads.data) ? resLeads.data : []);
      setSettings(resSettings.data);
      const cfg = resConfig.data || {};
      const ac = cfg.activecampaign;
      setAcStatus({
        configured: Boolean(ac?.configured ?? cfg.activecampaign_enabled),
        list_name: ac?.list_name ?? '',
        base_url: ac?.base_url ?? null,
      });
    } catch (e) {
      toast.error(e?.message || "Kunde inte hämta data.");
      setCases([]);
      setLeads([]);
      setAcStatus({ configured: false, list_name: '', base_url: null });
    } finally {
      setLoading(false);
    }
  };

  const runAnchorTest = async () => {
    setAnchorLoading(true);
    try {
      const res = await apiClient.get('/qa/anchor-test');
      setAnchorTest(res.data);
      if (res.data.in_range) {
        toast.success("Ankartestet PASSERADE!");
      } else {
        toast.warning(`Ankartestet MISSLYCKADES - ${res.data.suggestion}`);
      }
    } catch (e) {
      toast.error("Kunde inte köra ankartest");
    } finally {
      setAnchorLoading(false);
    }
  };

  const syncActiveCampaign = async () => {
    setAcLoading(true);
    try {
      const res = await apiClient.get('/activecampaign/sync', { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` } });
      setAcEmails(res.data);
      toast.success(`Synkat ${res.data.count} kontakter från ActiveCampaign`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte synka ActiveCampaign");
    } finally {
      setAcLoading(false);
    }
  };

  const exportAcEmails = () => {
    if (!acEmails?.emails) return;
    const csv = acEmails.emails.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activecampaign_${acEmails.listName}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const logout = () => {
      localStorage.removeItem('admin_token');
      navigate('/');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-500';
      case 'Booked': return 'bg-green-500';
      case 'Quoted': return 'bg-amber-500';
      default: return 'bg-slate-500';
    }
  };

  const priceAfterRot = (c) => {
    const privat = c.config?.customer_type === 'privat';
    const rot = c.result?.rot_deduction;
    const rec = c.result?.price_rec;
    if (!privat || rot == null || rot <= 0 || rec == null) return null;
    return Math.round(rec - rot);
  };

  const caseRouteId = (c) => c?.id ?? c?.case_id ?? c?.caseId ?? c?.case_number ?? null;

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* ActiveCampaign Status Banner */}
        {acStatus?.configured ? (
            <div className="bg-green-50 border border-green-200 p-4 rounded-md flex items-center gap-3 text-green-800">
                <CheckCircle className="h-5 w-5" />
                <span className="font-semibold">ActiveCampaign är konfigurerat</span>
                <span className="text-sm opacity-80">Lista: {acStatus.list_name}</span>
            </div>
        ) : (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-md flex flex-wrap items-center gap-x-3 gap-y-1 text-amber-800">
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <span className="font-semibold">ActiveCampaign ej konfigurerat</span>
                <span className="text-sm opacity-80 max-w-4xl">
                  Sätt ACTIVE_CAMPAIGN_BASE_URL och ACTIVE_CAMPAIGN_API_KEY i Vercel (frontend-projektet). Bokningsmail från wizarden skickas via SMTP och är oberoende av denna rad.
                </span>
            </div>
        )}

        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-950">
          <strong className="font-semibold">Kundflöde:</strong> I guiden är prisuppskattningen dold tills kunden skickat kontaktuppgifter; ärenden här har därför alltid kommit in efter det steget.{' '}
          <span className="font-medium">{PROMO_30_KITCHEN}</span> visas för privatkunder (ROT).
        </div>

        <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-primary">Adminpanel</h1>
            <div className="flex gap-2">
                 <Button variant="outline" onClick={() => navigate('/admin/calibration')}>
                    <Sliders className="mr-2 h-4 w-4" /> Kalibrering
                </Button>
                 <Button variant="outline" onClick={() => navigate('/admin/settings')}>
                    <Settings className="mr-2 h-4 w-4" /> Inställningar
                </Button>
                <Button onClick={fetchData} variant="outline"><RefreshCw className="mr-2 h-4 w-4" /> Uppdatera</Button>
                <Button variant="ghost" onClick={logout} title="Logga ut"><LogOut className="h-4 w-4" /></Button>
            </div>
        </div>

        {/* QA & Integrations Row */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Anchor Test Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" /> QA: Ankartest
              </CardTitle>
              <CardDescription>Standardkök (8U + 6V + 2H) ska ge 35-45k kr</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={runAnchorTest} disabled={anchorLoading} className="mb-3">
                {anchorLoading ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : null}
                Kör ankartest
              </Button>
              {anchorTest && (
                <div className={`p-3 rounded-lg ${anchorTest.in_range ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {anchorTest.in_range ? <CheckCircle className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
                    <span className="font-bold">{anchorTest.status}</span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div>
                      Pris:{' '}
                      <strong>
                        {anchorTest.actual_price != null && Number.isFinite(Number(anchorTest.actual_price))
                          ? `${Number(anchorTest.actual_price).toLocaleString('sv-SE')} kr`
                          : '-'}
                      </strong>
                    </div>
                    <div>
                      Mål:{' '}
                      <strong>
                        {typeof anchorTest.target_range === 'string' && anchorTest.target_range.trim()
                          ? anchorTest.target_range
                          : '-'}
                      </strong>
                    </div>
                    <div>
                      Timmar:{' '}
                      <strong>
                        {anchorTest.hours_median != null && Number.isFinite(Number(anchorTest.hours_median))
                          ? Number(anchorTest.hours_median)
                          : '-'}
                      </strong>
                      h @{' '}
                      <strong>
                        {anchorTest.hourly_rate != null && Number.isFinite(Number(anchorTest.hourly_rate))
                          ? `${Number(anchorTest.hourly_rate).toLocaleString('sv-SE')}`
                          : '-'}
                      </strong>{' '}
                      kr/h
                    </div>
                    {anchorTest.suggestion && <div className="text-amber-700 font-medium mt-2">{anchorTest.suggestion}</div>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ActiveCampaign Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" /> ActiveCampaign
              </CardTitle>
              <CardDescription>
                {acStatus?.configured 
                  ? `Lista: ${acStatus.list_name}` 
                  : 'Ej konfigurerad'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {acStatus?.configured ? (
                <>
                  <div className="flex gap-2 mb-3">
                    <Button onClick={syncActiveCampaign} disabled={acLoading}>
                      {acLoading ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                      Synka kontakter
                    </Button>
                    {acEmails && (
                      <Button variant="outline" onClick={exportAcEmails}>
                        <Download className="mr-2 h-4 w-4" /> Exportera CSV
                      </Button>
                    )}
                  </div>
                  {acEmails && (
                    <div className="bg-slate-50 p-3 rounded-lg border">
                      <div className="text-sm mb-2">
                        <strong>{acEmails.count}</strong> kontakter i listan "{acEmails.listName}"
                      </div>
                      {acEmails.lastSync && (
                        <div className="text-xs text-muted-foreground">
                          Senast synkat: {new Date(acEmails.lastSync).toLocaleString('sv-SE')}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  Lägg till ACTIVE_CAMPAIGN_* i Vercel-miljön för detta projekt för att aktivera synk (inte Python-backendens .env).
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="cases">
            <TabsList>
                <TabsTrigger value="cases">Ärenden ({cases.length})</TabsTrigger>
                <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="cases">
                <Card>
                    <CardHeader>
                        <CardTitle>Inkomna ärenden</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>ID</TableHead>
                                        <TableHead>Kund</TableHead>
                                        <TableHead>Datum</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>
                                            <div>Pris (rekomm.)</div>
                                            <div className="text-[10px] font-normal text-muted-foreground normal-case">{PROMO_30_KITCHEN} → efter ROT</div>
                                        </TableHead>
                                        <TableHead>Score</TableHead>
                                        <TableHead className="text-right">Åtgärd</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {cases.map((c) => {
                                        const rowKey = c.id || `case-${c.case_number}`;
                                        const contact = c.contact || {};
                                        const result = c.result || {};
                                        const completeness = result.completeness_percent ?? 0;
                                        const priceRec = result.price_rec;
                                        return (
                                        <TableRow key={rowKey}>
                                            <TableCell className="font-mono">#{c.case_number ?? '-'}</TableCell>
                                            <TableCell>
                                                <div className="font-medium">{contact.name ?? '-'}</div>
                                                <div className="text-xs text-muted-foreground">{contact.email ?? ''}</div>
                                                <div className="text-xs text-muted-foreground">{contact.phone ?? ''}</div>
                                                {(contact.address_street || contact.address_city) && (
                                                    <div className="text-xs text-muted-foreground mt-1">
                                                        {contact.address_street} {contact.address_number}
                                                        {contact.address_zip && `, ${contact.address_zip}`}
                                                        {contact.address_city && ` ${contact.address_city}`}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>{c.created_at ? new Date(c.created_at).toLocaleDateString('sv-SE') : '-'}</TableCell>
                                            <TableCell>
                                                <Badge className={getStatusColor(c.status)}>{c.status ?? '-'}</Badge>
                                            </TableCell>
                                            <TableCell>
                                                {priceRec != null && Number.isFinite(Number(priceRec)) ? (
                                                    <>
                                                        <div className="font-medium">{Number(priceRec).toLocaleString('sv-SE')} kr</div>
                                                        {priceAfterRot(c) != null ? (
                                                            <div className="text-xs text-green-700 font-semibold mt-0.5">
                                                                {priceAfterRot(c).toLocaleString('sv-SE')} kr efter ROT
                                                            </div>
                                                        ) : (
                                                            <div className="text-[10px] text-muted-foreground mt-0.5">Företag / ej ROT</div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-amber-600">Saknar prisdata</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className={`font-bold ${completeness < 60 ? 'text-amber-500' : 'text-green-600'}`}>
                                                    {completeness}%
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={!caseRouteId(c)}
                                                    onClick={() => navigate(`/admin/cases/${caseRouteId(c)}`)}
                                                >
                                                    Öppna
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })}
                                    {cases.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Inga ärenden än.</TableCell></TableRow>}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="leads">
                <Card>
                    <CardHeader>
                        <CardTitle>Intresseanmälningar (Quick Estimate)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Datum</TableHead>
                                        <TableHead>Namn</TableHead>
                                        <TableHead>Kontakt</TableHead>
                                        <TableHead>Kök</TableHead>
                                        <TableHead>Prismodell</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {leads.map((l) => (
                                        <TableRow key={l.id}>
                                            <TableCell>{new Date(l.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell className="font-medium">{l.contact_name}</TableCell>
                                            <TableCell>
                                                <div className="text-sm">{l.contact_phone}</div>
                                                <div className="text-xs text-muted-foreground">{l.contact_email}</div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="mr-1">{l.kitchen_size}</Badge>
                                                <Badge variant="outline">{l.high_cabinets} högskåp</Badge>
                                            </TableCell>
                                            <TableCell className="capitalize">{l.price_model}</TableCell>
                                        </TableRow>
                                    ))}
                                    {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Inga leads än.</TableCell></TableRow>}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
